import type {
  Acknowledgement,
  MonitoringSummary,
  WarningDetail,
  WarningPage,
} from '../consumer/contract';

type Validator = (value: unknown) => void;
function invalid(): never {
  throw new Error('Invalid NormalMonitoringV1 response');
}
const text: Validator = (v) => {
  if (typeof v !== 'string' || v.length > 16384) invalid();
};
const id: Validator = (v) => {
  text(v);
  if (v === '') invalid();
};
const number: Validator = (v) => {
  if (typeof v !== 'number' || !Number.isFinite(v)) invalid();
};
const count: Validator = (v) => {
  number(v);
  if (!Number.isSafeInteger(v) || (v as number) < 0) invalid();
};
const positive: Validator = (v) => {
  count(v);
  if (v === 0) invalid();
};
const boolean: Validator = (v) => {
  if (typeof v !== 'boolean') invalid();
};
const timestamp: Validator = (v) => {
  id(v);
  if (
    !/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?(?:Z|[+-]\d{2}:\d{2})$/.test(v as string) ||
    !Number.isFinite(Date.parse(v as string))
  )
    invalid();
};
const nullable =
  (validator: Validator): Validator =>
  (v) => {
    if (v !== null) validator(v);
  };
const oneOf =
  (...values: unknown[]): Validator =>
  (v) => {
    if (!values.includes(v)) invalid();
  };
const array =
  (validator: Validator, max = 10000): Validator =>
  (v) => {
    if (!Array.isArray(v) || v.length > max) invalid();
    (v as unknown[]).forEach(validator);
  };
const strings = array(text);
const record =
  (shape: Readonly<Record<string, Validator>>): Validator =>
  (v) => {
    if (v === null || typeof v !== 'object' || Array.isArray(v)) invalid();
    const obj = v as Record<string, unknown>;
    if (
      Object.keys(obj).some((key) => !(key in shape)) ||
      Object.keys(obj).length !== Object.keys(shape).length
    )
      invalid();
    for (const [key, validate] of Object.entries(shape)) validate(obj[key]);
  };
const area = record({
  areaId: id,
  sourceCount: count,
  recentSources: count,
  noDataSources: count,
  staleSources: count,
  disabledSources: count,
  activeWarningCount: count,
  availability: text,
});
const source = record({
  sourceId: id,
  areaId: id,
  category: id,
  metric: id,
  unit: id,
  health: oneOf('NO_DATA', 'RECENT', 'STALE', 'DISABLED'),
  latestObservedAt: nullable(timestamp),
  latestReceivedAt: nullable(timestamp),
  quality: nullable(text),
  enabled: boolean,
});
const summary = record({
  instanceId: id,
  profileVersion: oneOf('forest-synthetic-v1'),
  algorithmVersion: oneOf('RULE_BASELINE-v1'),
  generatedAt: timestamp,
  sourceMode: oneOf('SYNTHETIC_OBSERVATIONS'),
  acceptedCount: count,
  processedCount: count,
  duplicateCount: count,
  rejectedCount: count,
  pendingCount: count,
  activeWarningCount: count,
  totalWarningCount: count,
  areas: array(area, 100),
  sources: array(source, 1000),
  limitations: strings,
});
const warning = record({
  warningId: id,
  instanceId: id,
  areaId: id,
  episode: positive,
  revision: positive,
  state: oneOf('ACTIVE', 'INACTIVE'),
  createdAt: timestamp,
  updatedAt: timestamp,
  hypothesis: text,
  classification: text,
  supportObservationRefs: strings,
  conflictObservationRefs: strings,
  missingSourceIds: strings,
  sourceIds: strings,
  algorithmVersion: oneOf('RULE_BASELINE-v1'),
  profileVersion: oneOf('forest-synthetic-v1'),
  reason: text,
  acknowledged: boolean,
  limitations: strings,
});
const page = record({
  items: array(warning, 100),
  total: count,
  offset: count,
  limit: positive,
  generatedAt: timestamp,
});
const observationInput = record({
  instanceId: id,
  areaId: id,
  sourceId: id,
  observationId: id,
  observedAt: timestamp,
  metric: id,
  value: number,
  unit: id,
  quality: id,
  unknowns: strings,
  synthetic: oneOf(true),
  sourceVersion: id,
  adapterVersion: id,
  rawReference: id,
});
const observation = record({
  reference: id,
  input: observationInput,
  receivedAt: timestamp,
  processingStatus: id,
  processingReason: text,
});
const detection = record({
  detectionId: id,
  sourceId: id,
  areaId: id,
  observationRefs: strings,
  algorithmVersion: oneOf('RULE_BASELINE-v1'),
  windowStart: timestamp,
  windowEnd: timestamp,
  result: text,
  baseline: number,
  deviation: number,
  consecutiveAnomalies: count,
  consecutiveNormals: count,
  limitations: strings,
});
const acknowledgement = record({ warningId: id, revision: positive, actor: id, readAt: timestamp });
const detail = record({
  warning,
  revisions: array(warning),
  observations: array(observation),
  detections: array(detection),
  acknowledgements: array(acknowledgement),
});

export function parseSummary(value: unknown): MonitoringSummary {
  summary(value);
  return value as MonitoringSummary;
}
export function parseWarnings(value: unknown): WarningPage {
  page(value);
  const parsed = value as WarningPage;
  if (
    parsed.limit > 100 ||
    parsed.items.length > parsed.limit ||
    parsed.items.length > parsed.total
  )
    invalid();
  return parsed;
}
export function parseDetail(value: unknown): WarningDetail {
  detail(value);
  const parsed = value as WarningDetail;
  if (
    parsed.revisions.some(
      (item) =>
        item.warningId !== parsed.warning.warningId ||
        item.instanceId !== parsed.warning.instanceId ||
        item.areaId !== parsed.warning.areaId,
    ) ||
    parsed.acknowledgements.some((item) => item.warningId !== parsed.warning.warningId)
  )
    invalid();
  return parsed;
}
export function parseAcknowledgement(value: unknown): Acknowledgement {
  acknowledgement(value);
  return value as Acknowledgement;
}
